import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Zap, Train, Satellite, GraduationCap, Building2, Fuel, ArrowRight, Plane } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const INDUSTRIES = [
  {
    icon: Building2,
    title: 'Government',
    desc: 'Modernizing public sector operations with AI-driven policy analytics, citizen services automation, and smart city infrastructure.',
    image: 'https://images.unsplash.com/photo-1725272333872-4c2d274ad493?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    capabilities: ['Policy Analytics', 'Citizen Services AI', 'Smart City Systems', 'Document Intelligence']
  },
  {
    icon: Shield,
    title: 'Defense',
    desc: 'Building classified AI systems for intelligence analysis, threat detection, autonomous operations, and strategic decision support.',
    image: 'https://images.unsplash.com/photo-1503642153894-b3214047cd6e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    capabilities: ['Intelligence Analysis', 'Threat Detection', 'Autonomous Systems', 'Secure Communications']
  },
  {
    icon: Zap,
    title: 'Energy',
    desc: 'Optimizing power generation, grid management, and renewable energy integration through reinforcement learning and predictive models.',
    image: 'https://images.unsplash.com/photo-1758797849151-1725021be42a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    capabilities: ['Grid Optimization', 'Demand Forecasting', 'Renewable Integration', 'Smart Metering']
  },
  {
    icon: Fuel,
    title: 'Oil & Gas',
    desc: 'Deep learning for seismic analysis, reservoir modeling, pipeline monitoring, and operational safety enhancement.',
    image: 'https://images.unsplash.com/photo-1758797849151-1725021be42a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    capabilities: ['Seismic Analysis', 'Reservoir Modeling', 'Pipeline Monitoring', 'Safety Systems']
  },
  {
    icon: Train,
    title: 'Railways',
    desc: 'Predictive maintenance, real-time scheduling optimization, and passenger flow analysis for modern rail networks.',
    image: 'https://images.pexels.com/photos/10857006/pexels-photo-10857006.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    capabilities: ['Predictive Maintenance', 'Schedule Optimization', 'Passenger Analytics', 'Safety Monitoring']
  },
  {
    icon: Satellite,
    title: 'Satellite & Space',
    desc: 'Processing terabytes of satellite imagery daily for environmental monitoring, mapping, and intelligence gathering.',
    image: 'https://images.unsplash.com/photo-1633984726552-3ed7296dc5c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    capabilities: ['Imagery Analysis', 'Environmental Monitoring', 'Mapping Systems', 'Anomaly Detection']
  },
  {
    icon: GraduationCap,
    title: 'Education',
    desc: 'AI-powered research analytics, adaptive learning platforms, and institutional knowledge management systems.',
    image: 'https://images.unsplash.com/photo-1725272333872-4c2d274ad493?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    capabilities: ['Research Analytics', 'Adaptive Learning', 'Knowledge Discovery', 'Collaboration Tools']
  },
  {
    icon: Plane,
    title: 'Drones & Unmanned Systems',
    desc: 'Bespoke design and manufacturing of UAVs and UGVs for defense and commercial operations. We fuse satellite and drone intelligence to extract maximum data from every mission.',
    image: 'https://images.unsplash.com/photo-1640952635852-1c5f243ff4bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    capabilities: ['Custom UAV Design', 'Autonomous Navigation', 'Satellite-Drone Fusion', 'UGV Platforms', 'Swarm Intelligence']
  }
];

export default function IndustriesPage() {
  return (
    <div data-testid="industries-page">
      {/* Header */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-16 relative">
        <div className="absolute inset-0 bg-surface grid-bg" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.p {...fadeIn} className="text-xs uppercase tracking-[0.3em] text-brand-accent font-bold mb-4 md:mb-6">
            Sectors We Serve
          </motion.p>
          <motion.h1
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-heading font-black tracking-tight leading-[0.9] mb-6 md:mb-8"
          >
            Industry
            <br />
            <span className="text-gradient-cyan">Expertise</span>
          </motion.h1>
          <motion.p
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.2 }}
            className="text-lg text-slate-400 font-light max-w-xl"
          >
            Deep domain knowledge across critical sectors. We don&apos;t just build AI&mdash;we build AI that understands your industry.
          </motion.p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-4">
          {INDUSTRIES.map((industry, i) => (
            <motion.div
              key={industry.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`industry-${industry.title.toLowerCase().replace(/\s|&/g, '-')}`}
              className="glass-card rounded-lg overflow-hidden group"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12">
                {/* Image */}
                <div className="lg:col-span-4 h-48 lg:h-auto relative overflow-hidden">
                  <img
                    src={industry.image}
                    alt={industry.title}
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent" />
                  <div className="absolute bottom-6 left-6 flex items-center gap-3">
                    <industry.icon className="w-6 h-6 text-brand-accent" />
                    <h3 className="text-2xl font-heading font-bold">{industry.title}</h3>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:col-span-8 p-8 lg:p-10">
                  <p className="text-slate-400 text-sm leading-relaxed mb-6">{industry.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {industry.capabilities.map((cap) => (
                      <span key={cap} className="text-xs text-slate-500 border border-white/10 px-3 py-1.5 font-mono">
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-32 border-t border-white/5 section-glow">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight mb-6">
              Your Industry. Our Intelligence.
            </h2>
            <p className="text-lg text-slate-400 font-light max-w-lg mx-auto mb-12">
              Every sector has unique challenges. Let&apos;s build AI that speaks your language.
            </p>
            <Link
              to="/contact"
              data-testid="industries-cta"
              className="inline-flex items-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-10 py-5 hover:bg-slate-200 transition-colors duration-200"
            >
              Discuss Your Sector <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
